import { floor } from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import InfoItems, { InfoItem } from "../../../../components/InfoItems";
import { useHotspots } from "../../../../context/hotspots";
import {
  CellAvgSpeedtest,
  CellHeartbeat,
  SpeedTestTier,
  SpeedTestValidity
} from "../../../../context/hotspots/HotspotsProvider";
import classNames from "../../../../utils/classNames";
import CellStatus from "./CellStatus";

const Hotspot5GStatisticsTab: React.FC = () => {
  const { currentHotspot, fetchCellAvgSpeedTest, fetchCells } = useHotspots();
  const [cellAvgSpeedtest, setCellAvgSpeedtest] = useState<
    CellAvgSpeedtest | undefined
  >();
  const [loadingCells, setLoadingCells] = useState<boolean>(true);
  const [cells, setCells] = useState<CellHeartbeat[] | undefined>();

  const loadAvgSpeedTest = useCallback(() => {
    if (!currentHotspot) return;

    fetchCellAvgSpeedTest(currentHotspot.id).then((data) => {
      setCellAvgSpeedtest(data);
    });
  }, [currentHotspot, fetchCellAvgSpeedTest]);

  const loadCells = useCallback(() => {
    if (!currentHotspot) return;

    fetchCells(currentHotspot.id).then((data) => {
      setCells(data);
      setLoadingCells(false);
    });
  }, [currentHotspot, fetchCells]);

  useEffect(() => {
    loadAvgSpeedTest();
    loadCells();

    return () => {
      setCellAvgSpeedtest(undefined);
      setCells(undefined);
    };
  }, [loadAvgSpeedTest, loadCells]);

  const notEnoughData = useMemo(() => {
    if (!cellAvgSpeedtest) return false;

    return cellAvgSpeedtest.validity === SpeedTestValidity.TOO_FEW_SAMPLES;
  }, [cellAvgSpeedtest]);

  const multiplier = useMemo(() => {
    if (!cellAvgSpeedtest) return 0;

    return cellAvgSpeedtest.rewardMultiplier;
  }, [cellAvgSpeedtest]);

  const downloadTier = useMemo(() => {
    const speed = cellAvgSpeedtest?.downloadSpeedAvgBps;

    if (speed === undefined) return SpeedTestTier.FAIL;

    if (speed >= toBps(100)) return SpeedTestTier.ACCEPTABLE;

    if (speed >= toBps(50)) return SpeedTestTier.DEGRADED;

    if (speed >= toBps(30)) return SpeedTestTier.POOR;

    return SpeedTestTier.FAIL;
  }, [cellAvgSpeedtest?.downloadSpeedAvgBps]);

  const uploadTier = useMemo(() => {
    const speed = cellAvgSpeedtest?.uploadSpeedAvgBps;

    if (speed === undefined) return SpeedTestTier.FAIL;

    if (speed >= toBps(10)) return SpeedTestTier.ACCEPTABLE;

    if (speed >= toBps(5)) return SpeedTestTier.DEGRADED;

    if (speed >= toBps(2)) return SpeedTestTier.POOR;

    return SpeedTestTier.FAIL;
  }, [cellAvgSpeedtest?.uploadSpeedAvgBps]);

  const latencyTier = useMemo(() => {
    const latency = cellAvgSpeedtest?.latencyAvgMs;

    if (latency === undefined) return SpeedTestTier.FAIL;

    if (latency <= 50) return SpeedTestTier.ACCEPTABLE;

    if (latency <= 75) return SpeedTestTier.DEGRADED;

    if (latency <= 100) return SpeedTestTier.POOR;

    return SpeedTestTier.FAIL;
  }, [cellAvgSpeedtest?.latencyAvgMs]);

  const status = useMemo(() => {
    if (!cellAvgSpeedtest) return SpeedTestTier.NOT_AVAILABLE;

    if (notEnoughData) return SpeedTestTier.NOT_ENOUGH_DATA;

    if (
      downloadTier === SpeedTestTier.FAIL ||
      uploadTier === SpeedTestTier.FAIL ||
      latencyTier === SpeedTestTier.FAIL
    ) {
      return SpeedTestTier.FAIL;
    } else if (
      downloadTier === SpeedTestTier.POOR ||
      uploadTier === SpeedTestTier.POOR ||
      latencyTier === SpeedTestTier.POOR
    ) {
      return SpeedTestTier.POOR;
    } else if (
      downloadTier === SpeedTestTier.DEGRADED ||
      uploadTier === SpeedTestTier.DEGRADED ||
      latencyTier === SpeedTestTier.DEGRADED
    ) {
      return SpeedTestTier.DEGRADED;
    }
    return SpeedTestTier.ACCEPTABLE;
  }, [cellAvgSpeedtest, downloadTier, latencyTier, notEnoughData, uploadTier]);

  const speedTestGlobalInfoItems = useMemo(() => {
    const data: InfoItem[] = [
      {
        label: "Speed Test Rewards Multiplier",
        value: `${multiplier}x`,
        tooltip:
          "Speed Test multiplier is used together with Radio type multiplier to scale the MOBILE rewards."
      }
    ];

    if (cellAvgSpeedtest?.timestamp) {
      data.push({
        label: "Last measured",
        value: moment.unix(cellAvgSpeedtest.timestamp).fromNow()
      });
    }

    return data;
  }, [cellAvgSpeedtest, multiplier]);

  const speedTestStatusInfoItems = useMemo(() => {
    const data: InfoItem[] = [
      {
        label: "Avg Download Speed",
        value: statusItem(
          downloadTier,
          toMbps(cellAvgSpeedtest?.downloadSpeedAvgBps),
          [
            "Acceptable: 100+ Mbps",
            "Degraded: 50-99 Mbps",
            "Poor: 30-49 Mbps",
            "Failed: 0-29 Mbps"
          ]
        )
      },
      {
        label: "Avg Upload Speed",
        value: statusItem(
          uploadTier,
          toMbps(cellAvgSpeedtest?.uploadSpeedAvgBps),
          [
            "Acceptable: 10+ Mbps",
            "Degraded:  5-9 Mbps",
            "Poor: 2-4 Mbps",
            "Failed: 0-1 Mbps"
          ]
        )
      },
      {
        label: "Avg Latency",
        value: statusItem(
          latencyTier,
          `${cellAvgSpeedtest?.latencyAvgMs || 0} ms`,
          [
            "Acceptable: 0-50 ms",
            "Degraded: 51-75 ms",
            "Poor: 76-100 ms",
            "Failed: 101+ ms"
          ]
        )
      }
    ];

    return data;
  }, [
    cellAvgSpeedtest?.downloadSpeedAvgBps,
    cellAvgSpeedtest?.latencyAvgMs,
    cellAvgSpeedtest?.uploadSpeedAvgBps,
    downloadTier,
    latencyTier,
    uploadTier
  ]);

  if (!currentHotspot) return null;

  return (
    <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col pb-5 w-full justify-between border-b border-gray-200 dark:border-gray-500">
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
          Connected Radios
        </h3>
      </div>

      <div className="mt-4 flex flex-wrap sm:-mx-3">
        {loadingCells
          ? new Array(2)
              .fill(0)
              .map((_, i) => (
                <div
                  key={i}
                  className="w-full flex-1 sm:w-5/12 h-[142px] mb-6 mx-3 rounded bg-gray-300 dark:bg-gray-600 animate-pulse"
                ></div>
              ))
          : cells?.map((item) => (
              <div
                key={item.cellId}
                className="w-full px-0 sm:w-6/12 sm:px-3 mb-6"
              >
                <CellStatus cellHotspot={item} />
              </div>
            ))}
      </div>

      <div className="flex flex-col pb-5 w-full justify-between border-b border-gray-200 dark:border-gray-500">
        <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white flex items-center">
          Speed Test{" "}
          <span
            className={classNames(
              "ml-4 text-sm rounded-full px-2.5 text-white",
              speedTestTiers[status].badge
            )}
          >
            {status}
          </span>
        </h3>
      </div>

      {notEnoughData && (
        <span className="mt-2 text-sm text-gray-500 dark:text-gray-300">
          At least 2 Speed Tests are required
        </span>
      )}

      <InfoItems infoItems={speedTestGlobalInfoItems} className="mt-2" />

      <InfoItems infoItems={speedTestStatusInfoItems} className="mt-4 mb-2" />

      <a
        href="https://docs.helium.com/5g-on-helium/cbrs-radios"
        target="_blank"
        className="text-hotspotty-400 dark:text-hotspotty-200"
        rel="noreferrer"
      >
        For more information on Small Cell Radios and status, visit the docs.
      </a>
    </div>
  );
};

export default Hotspot5GStatisticsTab;

//
// Utils
//

export const statusItem = (
  tier: SpeedTestTier | undefined,
  value: string,
  items: string[]
) => {
  console.log(tier);
  console.log(items);
  return (
    <div className="flex flex-col items-start">
      <div className="flex items-center mb-1">
        <span className="font-bold text-lg">{value}</span>

        {tier && (
          <span
            className={classNames(
              "ml-2 text-sm rounded-full px-2.5 text-white",
              speedTestTiers[tier].badge
            )}
          >
            {tier}
          </span>
        )}
      </div>

      {items.map((item) => (
        <span
          className={classNames(
            tier === item.split(":")[0]
              ? `${speedTestTiers[tier].color} font-bold`
              : ""
          )}
          key={item}
        >
          {item}
        </span>
      ))}
    </div>
  );
};

export const speedTestTiers = {
  [SpeedTestTier.ACCEPTABLE]: {
    badge: "bg-green-500",
    color: "text-green-500"
  },
  [SpeedTestTier.DEGRADED]: {
    badge: "bg-yellow-500",
    color: "text-yellow-500"
  },
  [SpeedTestTier.POOR]: {
    badge: "bg-red-500",
    color: "text-red-500"
  },
  [SpeedTestTier.FAIL]: {
    badge: "bg-gray-500",
    color: "text-gray-500"
  },
  [SpeedTestTier.NOT_ENOUGH_DATA]: {
    badge: "bg-gray-600",
    color: "text-gray-600"
  },
  [SpeedTestTier.NOT_AVAILABLE]: { badge: "", color: "" }
};

export const toMbps = (bps: number | undefined) =>
  bps ? `${floor((bps * 8) / 10e5)} Mbps` : "0 Mbps";

export const toBps = (mbps: number) => mbps * 125000;
